import { api } from '@/v2/plugins/axios';
import { defineStore } from 'pinia';
import { t } from '@/locales/index';
import { Alert } from '@/v2/plugins/alert';
import { featureFlag, isFeatureFlagEnabled } from '@/v2/entities/feature-flag';
import { router } from '@/v2/router';

const ELITE_PACKAGES = {
	yearlyElitePackage: {
		id: 9999999999,
		duration_unit: 'YEAR',
		type: 'ELITE',
		title: t('elite-package'),
	},
	monthlyElitePackage: {
		id: 8888888888,
		duration_unit: 'MONTH',
		type: 'ELITE',
		title: t('elite-package'),
	},
};

export const abilities = Object.freeze({
	CreateOrder: 'create_order',
	CreatePickupLocation: 'create_pickup_location',
	CreateProduct: 'create_product',
	CreateUser: 'create_user',
	LinkNewMarketplace: 'link_new_marketplace',
});

export const useLimitationsStore = defineStore('limitations', {
	state: () => ({
		isDialogOpen: false,
		isLoading: false,
		limitations: {},
		packages: [],
		currentPackage: null,
		dialogTitle: '',
	}),
	getters: {
		isFreeTrial() {
			return (
				this.currentPackage?.duration_unit === 'DAYS' &&
				!this.limitations.has_active_subscription
			);
		},
		isSubscriptionEnd() {
			return (
				this.currentPackage?.duration_unit !== 'DAYS' &&
				!this.limitations.has_active_subscription
			);
		},
	},
	actions: {
		openDialog() {
			this.isDialogOpen = true;
		},
		closeDialog() {
			this.isDialogOpen = false;
		},
		canDo(ability) {
			return this.limitations[`can_${ability}`];
		},
		getErrorText(ability) {
			if (ability == abilities.CreateOrder) return t('limit-message.orders');
			if (ability == abilities.CreateUser) return t('limit-message.users');
			if (ability == abilities.CreateProduct) return t('limit-message.products');
			if (ability == abilities.LinkNewMarketplace)
				return t('limit-message.linked_sales_channels');
			if (ability == abilities.CreatePickupLocation) return t('limit-message.pick_locations');

			return '';
		},
		async getLimitations() {
			const response = await api.get('/subscription/check-limitation');
			this.limitations = response.data.result;
		},
		async checkLimitation(ability) {
			return new Promise(async (resolve, reject) => {
				await this.getLimitations();

				if (!this.canDo(ability)) {
					if (isFeatureFlagEnabled(featureFlag.AI_AGENT)) {
						router.push('/v2/core/change-plan');
					} else {
						await this.getPackages(ability);
						this.isDialogOpen = true;
					}
					reject();
				} else {
					resolve(true);
				}
			});
		},
		async getPackages(ability) {
			const { data } = await api.get('/subscription/packages/change', {
				params: {
					sort_column: 'price',
					sort_direction: 'asc',
				},
			});

			let packages = data.result.availablePackages;
			packages = packages.filter((p) => p.product_type == 'linker_sellers');
			packages.push(ELITE_PACKAGES.yearlyElitePackage);
			packages.push(ELITE_PACKAGES.monthlyElitePackage);

			this.packages = packages;
			this.currentPackage = data.result.currentPackage;

			if (this.isFreeTrial || this.isSubscriptionEnd) {
				this.dialogTitle = this.isSubscriptionEnd
					? t('limit-message.subscription_ended')
					: t('limit-message.trial_ended');
			} else {
				this.dialogTitle = this.getErrorText(ability);
			}
		},
		async contactSales(form) {
			try {
				await api.post('/subscription/packages/elite/support-info', form);
				this.closeDialog();
				Alert.success(t('success-msg'));
			} catch (error) {
				Alert.error(error?.response?.data?.message);
			}
		},
	},
});
